/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Voice Cloning Audio Ingestion & Creation Modal (Task 10.5.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef } from 'react';
import {
  X,
  Mic,
  UploadCloud,
  FileAudio,
  Check,
  AlertCircle,
  Coins,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useVoiceCatalogStore, VoiceGender } from '../../stores/useVoiceCatalogStore';

interface NewVoiceCloneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVoiceCloned?: (voiceId: string) => void;
}

export const NewVoiceCloneModal: React.FC<NewVoiceCloneModalProps> = ({
  isOpen,
  onClose,
  onVoiceCloned,
}) => {
  const { t } = useTranslation();
  const { createClonedVoice } = useVoiceCatalogStore();

  const [name, setName] = useState('');
  const [language, setLanguage] = useState('vi-VN');
  const [gender, setGender] = useState<VoiceGender>('female');
  const [accentRegion, setAccentRegion] = useState('');
  const [description, setDescription] = useState('');
  const [sampleScript, setSampleScript] = useState('');

  const [audioFile, setAudioFile] = useState<{
    name: string;
    size: number;
  } | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFile = (file: File) => {
    setErrorMessage(null);
    if (!file.type.includes('audio') && !file.name.match(/\.(mp3|wav|m4a|ogg|aac|flac)$/i)) {
      setErrorMessage(t('voice.errorInvalidAudio'));
      return;
    }

    setAudioFile({
      name: file.name,
      size: file.size,
    });

    if (!name) {
      setName(file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    }
  };

  const handleCreateClone = () => {
    if (!name.trim()) {
      setErrorMessage(t('voice.errorEnterName'));
      return;
    }
    if (!audioFile) {
      setErrorMessage(t('voice.errorUploadAudio'));
      return;
    }

    setIsCloning(true);
    setTimeout(() => {
      const newVoice = createClonedVoice({
        name: name.trim(),
        language,
        gender,
        accentRegion: accentRegion.trim() || 'Custom Workspace Voice',
        description: description.trim() || 'Custom cloned AI voice profile.',
        sampleScript: sampleScript.trim() || undefined,
      });

      setIsCloning(false);
      onVoiceCloned?.(newVoice.id);
      onClose();
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-xl bg-surface border border-surface-border rounded-xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-surface-border bg-surface-elevated/40">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10 text-primary border border-primary/20">
              <Mic className="size-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">
                {t('voice.newCloneTitle')}
              </h2>
              <p className="text-xs text-foreground-muted">
                {t('voice.newCloneDesc')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-foreground-muted hover:text-foreground rounded-lg hover:bg-surface-elevated transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {errorMessage && (
            <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-rose-500/10 text-rose-500 border border-rose-500/20">
              <AlertCircle className="size-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Audio Reference Upload Area */}
          <div>
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-2">
              {t('voice.uploadSampleAudioLabel')}
            </label>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`p-6 border-2 border-dashed rounded-xl cursor-pointer text-center transition-all ${
                isDragging
                  ? 'border-primary bg-primary/10'
                  : audioFile
                  ? 'border-emerald-500/40 bg-emerald-500/5'
                  : 'border-surface-border bg-surface-elevated/30 hover:border-surface-border/80'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.ogg,.aac"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
                }}
                className="hidden"
              />

              {audioFile ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-500">
                    <FileAudio className="size-6" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-foreground">
                      {audioFile.name}
                    </p>
                    <p className="text-[11px] text-foreground-muted">
                      {(audioFile.size / (1024 * 1024)).toFixed(2)} MB &bull; Sample Verified
                    </p>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-emerald-500 font-semibold">
                    <Check className="size-3.5" />
                    {t('voice.audioReady')}
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="size-10 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <UploadCloud className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-foreground">
                      {t('voice.dragDropAudio')}
                    </p>
                    <p className="text-[11px] text-foreground-muted mt-0.5">
                      {t('voice.supportedAudioFormats')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Name & Accent */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
                {t('voice.cloneNameLabel')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('voice.cloneNamePlaceholder')}
                className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
                {t('voice.accentLabel')}
              </label>
              <input
                type="text"
                value={accentRegion}
                onChange={(e) => setAccentRegion(e.target.value)}
                placeholder={t('voice.accentPlaceholder')}
                className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Language & Gender */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
                {t('voice.languageLabel')}
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="vi-VN">{t('cat.langVi')}</option>
                <option value="en-US">{t('cat.langEn')}</option>
                <option value="ja-JP">{t('cat.langJa')}</option>
                <option value="zh-CN">{t('cat.langZh')}</option>
                <option value="fr-FR">{t('cat.langFr')}</option>
                <option value="de-DE">{t('cat.langDe')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
                {t('voice.genderLabel')}
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value as VoiceGender)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="female">{t('voice.genderFemale')}</option>
                <option value="male">{t('voice.genderMale')}</option>
                <option value="neutral">{t('voice.genderNeutral')}</option>
              </select>
            </div>
          </div>

          {/* Description & Reference Text */}
          <div>
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
              {t('voice.descriptionLabel')}
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('voice.descriptionPlaceholder')}
              className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary resize-none"
            />
          </div>

          {/* Sample Audition Script */}
          <div>
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
              {t('voice.cloneSampleScript')}
            </label>
            <input
              type="text"
              value={sampleScript}
              onChange={(e) => setSampleScript(e.target.value)}
              placeholder={t('voice.cloneSampleScriptPlaceholder')}
              className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Flat fee quote notice */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs">
            <div className="flex items-center gap-2">
              <Sparkles className="size-4 text-primary" />
              <span className="font-medium text-foreground">
                {t('voice.cloneCreationFeeNotice')}
              </span>
            </div>
            <div className="flex items-center gap-1 font-mono font-bold text-primary">
              <Coins className="size-3.5 text-amber-500" />
              <span>50 credits</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 p-4 border-t border-surface-border bg-surface-elevated/40">
          <button
            type="button"
            onClick={onClose}
            disabled={isCloning}
            className="px-4 py-2 text-xs font-medium rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            {t('cat.closeModal')}
          </button>
          <button
            type="button"
            onClick={handleCreateClone}
            disabled={isCloning || !audioFile}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-primary text-primary-fg hover:bg-primary/90 shadow-sm transition-colors disabled:opacity-50"
          >
            {isCloning ? (
              <>
                <div className="size-3.5 border-2 border-primary-fg/30 border-t-primary-fg rounded-full animate-spin" />
                <span>{t('voice.trainingCloneModel')}</span>
              </>
            ) : (
              <>
                <Mic className="size-3.5" />
                <span>{t('voice.startCloningBtn')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
